import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { AlertTriangle, Layers, RefreshCw } from "lucide-react";

/**
 * Diálogo genérico para consolidar múltiples documentos (Notas de Pedido o Cotizaciones)
 * pertenecientes al mismo cliente, en un único set de líneas agrupadas por
 * (producto + lote + unidad_venta) sumando cantidades.
 *
 * Props:
 * - open: boolean
 * - onClose: fn()
 * - clienteId: string (obligatorio)
 * - excluirId: string (opcional — típicamente el doc origen ya cargado)
 * - kind: "pedido" | "cotizacion"
 * - onConsolidated(data): callback con la respuesta del backend (líneas + ids)
 */
export function ConsolidateOrdersDialog({ open, onClose, clienteId, excluirId, kind, onConsolidated }) {
  const isPedido = kind === "pedido";
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const listUrl = isPedido ? "/sales/pedidos-consolidables" : "/sales/cotizaciones-consolidables";
  const consolidateUrl = isPedido ? "/sales/pedidos/consolidar" : "/sales/cotizaciones/consolidar";
  const label = isPedido ? "Notas de Pedido" : "Cotizaciones";
  const labelSingular = isPedido ? "nota de pedido" : "cotización";

  useEffect(() => {
    if (!open || !clienteId) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ cliente_id: clienteId });
        if (excluirId) params.append("excluir_id", excluirId);
        const { data } = await api.get(`${listUrl}?${params.toString()}`);
        if (mounted) {
          setItems(data || []);
          const sel = {};
          if (excluirId) sel[excluirId] = true;  // marcar el actual visualmente aunque no venga en la lista
          setSelected(sel);
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [open, clienteId, excluirId, listUrl]);

  const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    if (items.every(x => selected[x.id])) {
      setSelected({});
    } else {
      const all = {};
      items.forEach(x => { all[x.id] = true; });
      setSelected(all);
    }
  };

  const idsSeleccionados = useMemo(
    () => Object.entries(selected).filter(([, v]) => v).map(([k]) => k).filter(id => id !== excluirId || items.some(x => x.id === id)),
    [selected, excluirId, items]
  );

  // Para consolidar, incluimos también excluirId si el usuario quiere fusionarlo
  const idsParaConsolidar = useMemo(() => {
    const list = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (excluirId && !list.includes(excluirId)) list.push(excluirId);
    return list;
  }, [selected, excluirId]);

  const consolidar = async () => {
    if (idsSeleccionados.length === 0) {
      toast.warning(`Debes seleccionar al menos una ${labelSingular} adicional para consolidar`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(consolidateUrl, { ids: idsParaConsolidar });
      onConsolidated(data);
      toast.success(`Consolidadas ${idsParaConsolidar.length} ${label} · ${data.lineas?.length || 0} línea(s)`);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const totalLineasSel = items
    .filter(x => selected[x.id])
    .reduce((sum, x) => sum + (x.lineas?.length || 0), 0);
  const totalSubtotalSel = items
    .filter(x => selected[x.id])
    .reduce((sum, x) => sum + (Number(x.subtotal) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="consolidate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers size={18} className="text-[#205c45]" />
            Consolidar {label}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-sm text-[#5c7368] bg-[#f7f9f8] border border-[#d1dbd6] rounded-md p-3">
            Las líneas se fusionarán por <b>producto + lote + unidad de venta</b>, sumando cantidades.
            Si dos {label.toLowerCase()} tienen precios distintos para el mismo producto+lote se conservará el precio de la primera y verás una advertencia.
          </div>

          {loading ? (
            <div className="text-center py-6 text-sm text-[#5c7368]">
              <RefreshCw size={18} className="inline animate-spin mr-2" /> Cargando…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-6 text-sm text-[#5c7368]" data-testid="consolidate-empty">
              No hay otras {label.toLowerCase()} del mismo cliente para consolidar.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs text-[#5c7368]">
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-[#205c45] hover:underline"
                  data-testid="consolidate-toggle-all"
                >
                  {items.every(x => selected[x.id]) ? "Deseleccionar todo" : "Seleccionar todo"}
                </button>
                <span>{idsSeleccionados.length} de {items.length} · {totalLineasSel} línea(s) · ${totalSubtotalSel.toFixed(2)}</span>
              </div>
              <div className="max-h-72 overflow-y-auto border border-[#d1dbd6] rounded-md divide-y">
                {items.map((it) => (
                  <label
                    key={it.id}
                    className={`flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-[#f7f9f8] ${selected[it.id] ? "bg-[#e9f2ee]" : ""}`}
                    data-testid={`consolidate-row-${it.numero || it.id}`}
                  >
                    <Checkbox
                      checked={!!selected[it.id]}
                      onCheckedChange={() => toggle(it.id)}
                      data-testid={`consolidate-check-${it.numero || it.id}`}
                    />
                    <div className="flex-1 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-[#205c45]">{it.numero || it.id.slice(0, 8)}</span>
                        <span className="text-[11px] text-[#5c7368]">· {it.fecha}</span>
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#eef3f1] text-[#205c45] border border-[#d1dbd6]">
                          {it.estado}
                        </span>
                      </div>
                      <div className="text-[11px] text-[#5c7368] mt-0.5">
                        {it.lineas?.length || 0} línea(s) · ${(it.subtotal || 0).toFixed(2)}
                        {it.vendedor_nombre && <> · {it.vendedor_nombre}</>}
                      </div>
                      {it.observaciones && (
                        <div className="text-[11px] text-[#5c7368] mt-0.5 italic line-clamp-1">&ldquo;{it.observaciones}&rdquo;</div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}

          {excluirId && (
            <div className="text-[11px] text-[#8a5500] bg-[#fef7ec] border border-[#f5c477] rounded-md px-3 py-2 flex items-center gap-2">
              <AlertTriangle size={14} />
              La {labelSingular} actual también se incluirá en la consolidación.
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="consolidate-cancel-btn">
            Cancelar
          </Button>
          <Button
            onClick={consolidar}
            disabled={busy || idsSeleccionados.length === 0}
            className="bg-[#205c45] hover:bg-[#1a4a37]"
            data-testid="consolidate-confirm-btn"
          >
            {busy ? "Consolidando…" : `Consolidar ${idsParaConsolidar.length} ${label}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
